import { Link, router, useForm } from "@inertiajs/react"
import { ExternalLink, Trash2 } from "lucide-react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Textarea } from "@/components/ui/textarea"
import type { StudioEventRow, StudioPoiRow, StudioUrls } from "@/lib/types"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TIMEZONES = [
  { value: "Europe/Lisbon", label: "Lisbon (WET)" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Paris", label: "Paris (CET)" },
  { value: "Europe/Berlin", label: "Berlin (CET)" },
  { value: "America/New_York", label: "New York (ET)" },
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toDatetimeLocal(iso: string): string {
  return iso.slice(0, 16) // "2026-06-15T14:30" format
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EventSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  event: StudioEventRow | null // null = create mode
  pois: StudioPoiRow[] // for venue autocomplete
  urls: StudioUrls
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EventSheet({ open, onOpenChange, event, pois, urls }: EventSheetProps) {
  const isEdit = event !== null

  const form = useForm({
    name: event?.name ?? "",
    description: event?.description ?? "",
    starts_at: event?.starts_at ? toDatetimeLocal(event.starts_at) : "",
    ends_at: event?.ends_at ? toDatetimeLocal(event.ends_at) : "",
    timezone: event?.timezone ?? "Europe/Lisbon",
    external_url: event?.external_url ?? "",
  })

  // Nest under "event" key for Rails strong params
  form.transform((data) => ({ event: data }))

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    if (isEdit) {
      form.patch(event.update_url, {
        onSuccess: () => onOpenChange(false),
        preserveScroll: true,
      })
    } else {
      form.post(urls.create_event, {
        onSuccess: () => onOpenChange(false),
        preserveScroll: true,
      })
    }
  }

  function performDelete() {
    if (!event) return

    router.delete(event.destroy_url, {
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
    })
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[580px]" showCloseButton>
        <SheetHeader>
          <SheetTitle>{isEdit ? `Edit · ${event.name}` : "Add a new event"}</SheetTitle>
          <SheetDescription>
            Public event visible to every property in this region
          </SheetDescription>
        </SheetHeader>

        <form
          id="event-sheet-form"
          onSubmit={handleSubmit}
          className="flex-1 space-y-5 overflow-y-auto px-4"
        >
          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="event-name">Name *</Label>
            <Input
              id="event-name"
              value={form.data.name}
              onChange={(e) => form.setData("name", e.target.value)}
              placeholder="e.g. Fado Night at Tasca do Chico"
              required
            />
            {form.errors.name && (
              <p className="text-sm text-destructive">{form.errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="event-description">Description</Label>
            <Textarea
              id="event-description"
              value={form.data.description}
              onChange={(e) => form.setData("description", e.target.value)}
              placeholder="A short description guests will see in the guide."
              rows={3}
            />
            {form.errors.description && (
              <p className="text-sm text-destructive">{form.errors.description}</p>
            )}
          </div>

          {/* Starts at / Ends at */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="event-starts-at">Starts at *</Label>
              <Input
                id="event-starts-at"
                type="datetime-local"
                value={form.data.starts_at}
                onChange={(e) => form.setData("starts_at", e.target.value)}
                required
              />
              {form.errors.starts_at && (
                <p className="text-sm text-destructive">{form.errors.starts_at}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="event-ends-at">Ends at</Label>
              <Input
                id="event-ends-at"
                type="datetime-local"
                value={form.data.ends_at}
                onChange={(e) => form.setData("ends_at", e.target.value)}
              />
              {form.errors.ends_at && (
                <p className="text-sm text-destructive">{form.errors.ends_at}</p>
              )}
            </div>
          </div>

          {/* Timezone */}
          <div className="space-y-1.5">
            <Label htmlFor="event-timezone">Timezone</Label>
            <Select
              value={form.data.timezone}
              onValueChange={(v) => form.setData("timezone", v)}
            >
              <SelectTrigger id="event-timezone">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.errors.timezone && (
              <p className="text-sm text-destructive">{form.errors.timezone}</p>
            )}
          </div>

          {/* External URL */}
          <div className="space-y-1.5">
            <Label htmlFor="event-external-url">External URL</Label>
            <Input
              id="event-external-url"
              value={form.data.external_url}
              onChange={(e) => form.setData("external_url", e.target.value)}
              placeholder="https://tickets.example.com/..."
            />
            {form.errors.external_url && (
              <p className="text-sm text-destructive">{form.errors.external_url}</p>
            )}
          </div>

          {/* Venue (poi) — informational select, not submitted (poi_id managed separately) */}
          {pois.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-muted-foreground">
                Linked venue (read-only)
              </Label>
              <p className="text-sm text-muted-foreground">
                {event?.poi_name ?? "No venue linked"}
              </p>
            </div>
          )}
        </form>

        {isEdit && (
          <div className="border-t px-1 py-3">
            <Link
              href={event.edit_url}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              <ExternalLink className="size-3.5" />
              Open full editor
            </Link>
          </div>
        )}

        <SheetFooter className="flex-row justify-between border-t pt-4">
          <div>
            {isEdit && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" variant="destructive" size="sm">
                    <Trash2 className="mr-1.5 size-4" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this event?</AlertDialogTitle>
                    <AlertDialogDescription>
                      “{event.name}” will be removed from every guide in this
                      region. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={performDelete}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete event
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="event-sheet-form"
              size="sm"
              disabled={form.processing}
            >
              {form.processing
                ? "Saving..."
                : isEdit
                  ? "Save changes"
                  : "Create event"}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
